import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import styled from "styled-components";
import { MdEmail, MdRefresh, MdDelete, MdSearch } from "react-icons/md";
import { useTable, useSortBy, usePagination } from "react-table";
import useAxios from "../../customHooks/useAxios";
import ToastAlertView from "../common/alert/ToastAlertView";
import PaginationHandler from "../common/pagination/PaginationHandler";
import ConfirmModal from "../common/confirm/ConfirmModal";
import FailedEmailScheduleDetailModal from "./modals/FailedEmailScheduleDetailModal";
import "./FailedEmailScheduleManagementStyle.css";

// Styled Components
const SearchInput = styled.input`
    width: 100%;
    height: 100%;
    padding: 0 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: #fc7242;
    }
`;

const FilterSelect = styled.select`
    width: 100%;
    height: 100%;
    padding: 0 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #fc7242;
    }
`;

const SearchButton = styled.button`
    width: 100%;
    height: 100%;
    background-color: #fc7242;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s;

    &:hover {
        background-color: #e55a2b;
    }
`;

const TableContainer = styled.div`
    width: 100%;
    height: 600px;
    overflow-y: auto;
    border: 1px solid #ebebeb;
    border-radius: 5px;
    background-color: white;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    thead {
        background-color: #f8f9fa;

        th {
            padding: 12px 15px;
            text-align: left;
            font-weight: 600;
            font-size: 14px;
            color: #333;
            border-bottom: 2px solid #ebebeb;
            cursor: pointer;
            user-select: none;

            &:hover {
                background-color: #f0f0f0;
            }
        }
    }

    tbody {
        tr {
            border-bottom: 1px solid #ebebeb;
            transition: background-color 0.2s;
            cursor: pointer;

            &:hover {
                background-color: #fff8f6;
            }

            &:last-child {
                border-bottom: none;
            }

            td {
                padding: 12px 15px;
                font-size: 14px;
                color: #555;
            }
        }
    }
`;

const ActionButton = styled.button`
    padding: 6px 12px;
    margin-right: 8px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: all 0.2s;
    background-color: ${props => props.variant === 'retry' ? '#28a745' : '#dc3545'};
    color: white;

    &:hover {
        background-color: ${props => props.variant === 'retry' ? '#218838' : '#c82333'};
    }
`;

const EmptyMessage = styled.div`
    padding: 60px 20px;
    text-align: center;
    color: #999;
    font-size: 14px;
`;

const EmailTypeBadge = styled.span`
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${props => props.type === 'ORDER' ? '#e3f2fd' : '#f3e5f5'};
    color: ${props => props.type === 'ORDER' ? '#1976d2' : '#7b1fa2'};
`;

const TruncatedText = styled.div`
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FailedEmailScheduleManagement = () => {
    const { isAxiosLoading, axiosCall } = useAxios();
    const { t } = useTranslation();

    // List data state
    const [failedEmailList, setFailedEmailList] = useState([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter and search state
    const [emailType, setEmailType] = useState("all");
    const [searchKeyword, setSearchKeyword] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortTarget, setSortTarget] = useState("registerTime");

    // Modal state
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [isRetryConfirm, setIsRetryConfirm] = useState(false);
    const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
    const [actionEmail, setActionEmail] = useState(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [retryingEmailId, setRetryingEmailId] = useState(null);

    // Fetch failed email schedule list
    const fetchFailedEmailScheduleList = async () => {
        try {
            const params = {
                page: currentPageIndex,
                size: 12,
                email_type: emailType,
                sort: sortOrder,
                sort_target: sortTarget
            };

            if (searchKeyword) {
                params.field = "emailTitle";
                params.like = searchKeyword;
            }

            const res = await axiosCall(
                "GET",
                "/failedEmailSchedule",
                [],
                {},
                null,
                params,
                "default"
            );

            if (res.data.error_code === "NO_ERROR") {
                setFailedEmailList(res.data.item.failedEmailScheduleList || []);
                setTotalElements(res.data.item.totalItems || 0);
                setTotalPages(res.data.item.totalPages || 0);
            } else if (res.data.error_code === "GET_EMPTY_DATA") {
                setFailedEmailList([]);
                setTotalElements(0);
                setTotalPages(0);
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    // Retry failed email
    const handleRetry = async () => {
        if (!actionEmail || isRetrying) return;

        setIsRetrying(true);
        setRetryingEmailId(actionEmail.failedEmailScheduleId);
        try {
            const res = await axiosCall(
                "POST",
                "/failedEmailSchedule/:1/retry",
                [actionEmail.failedEmailScheduleId],
                {},
                null,
                {},
                "default"
            );

            if (res.data.error_code === "NO_ERROR" && res.data.item.success) {
                toast.success(<ToastAlertView message={t("failedEmailSchedule.retrySuccess")} />);
                setIsRetryConfirm(false);
                setActionEmail(null);
                fetchFailedEmailScheduleList();
            } else {
                toast.error(<ToastAlertView message={t("failedEmailSchedule.retryFailed")} />);
                setIsRetryConfirm(false);
                setActionEmail(null);
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
            setIsRetryConfirm(false);
            setActionEmail(null);
        } finally {
            setIsRetrying(false);
            setRetryingEmailId(null);
        }
    };

    // Delete failed email schedule
    const handleDelete = async () => {
        if (!actionEmail) return;

        try {
            const res = await axiosCall(
                "DELETE",
                "/failedEmailSchedule/:1",
                [actionEmail.failedEmailScheduleId],
                {},
                null,
                {},
                "default"
            );

            if (res.data.error_code === "NO_ERROR") {
                toast.success(<ToastAlertView message={t("message.deleted")} />);
                setIsDeleteConfirm(false);
                setActionEmail(null);
                fetchFailedEmailScheduleList();
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    const handleSearch = () => {
        setSearchKeyword(searchInput);
        setCurrentPageIndex(0);
    };

    const handleRowClick = (email) => {
        setSelectedEmail(email);
        setIsDetailModalOpen(true);
    };

    const handleRetryClick = (e, email) => {
        e.stopPropagation();
        setActionEmail(email);
        setIsRetryConfirm(true);
    };

    const handleDeleteClick = (e, email) => {
        e.stopPropagation();
        setActionEmail(email);
        setIsDeleteConfirm(true);
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                Header: t("failedEmailSchedule.emailType"),
                accessor: "emailType",
                Cell: ({ value }) => (
                    <EmailTypeBadge type={value}>
                        {t(`failedEmailSchedule.emailTypes.${value}`)}
                    </EmailTypeBadge>
                )
            },
            {
                Header: t("failedEmailSchedule.emailTitle"),
                accessor: "emailTitle",
                Cell: ({ value }) => <TruncatedText>{value}</TruncatedText>
            },
            {
                Header: t("failedEmailSchedule.registerTime"),
                accessor: "registerTime",
                Cell: ({ value }) => new Date(value).toLocaleString()
            },
            {
                Header: t("failedEmailSchedule.failureReason"),
                accessor: "failureReason",
                Cell: ({ value }) => <TruncatedText>{value}</TruncatedText>
            },
            {
                Header: t("common.actions"),
                accessor: "actions",
                disableSortBy: true,
                Cell: ({ row }) => {
                    const isThisRetrying = retryingEmailId === row.original.failedEmailScheduleId;
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ActionButton
                                variant="retry"
                                onClick={(e) => handleRetryClick(e, row.original)}
                                disabled={isThisRetrying || isRetrying}
                                style={{
                                    opacity: (isThisRetrying || isRetrying) ? 0.6 : 1,
                                    cursor: (isThisRetrying || isRetrying) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <MdRefresh size={16} />
                                {isThisRetrying ? t("button.processing") : t("failedEmailSchedule.retry")}
                            </ActionButton>
                            <ActionButton
                                variant="delete"
                                onClick={(e) => handleDeleteClick(e, row.original)}
                                disabled={isRetrying}
                                style={{
                                    opacity: isRetrying ? 0.6 : 1,
                                    cursor: isRetrying ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <MdDelete size={16} />
                                {t("failedEmailSchedule.delete")}
                            </ActionButton>
                        </div>
                    );
                }
            }
        ],
        [t, isRetrying, retryingEmailId]
    );

    // Effects
    useEffect(() => {
        fetchFailedEmailScheduleList();
    }, [currentPageIndex, emailType, searchKeyword, sortOrder, sortTarget]);

    return (
        <Container fluid>
            <Row>
                <div style={{
                    width: '100%',
                    minHeight: '800px',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Control Bar */}
                    <div className={"contentInner"} style={{ width: '100%', display: 'flex' }}>
                        <div style={{ width: '200px', height: '50px' }}>
                            <FilterSelect
                                value={emailType}
                                onChange={(e) => {
                                    setEmailType(e.target.value);
                                    setCurrentPageIndex(0);
                                }}
                            >
                                <option value="all">{t("failedEmailSchedule.emailTypes.all")}</option>
                                <option value="ORDER">{t("failedEmailSchedule.emailTypes.ORDER")}</option>
                                <option value="DEVELOPMENT">{t("failedEmailSchedule.emailTypes.DEVELOPMENT")}</option>
                            </FilterSelect>
                        </div>
                        <div style={{ width: '350px', height: '50px', marginLeft: '10px' }}>
                            <SearchInput
                                type="text"
                                placeholder={t("failedEmailSchedule.searchPlaceholder")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleSearch();
                                    }
                                }}
                            />
                        </div>
                        <div style={{ width: '50px', height: '50px', marginLeft: '10px' }}>
                            <SearchButton onClick={handleSearch}>
                                <MdSearch size={18} />
                            </SearchButton>
                        </div>
                    </div>

                    {/* Table */}
                    <div className={"contentInner"} style={{ marginTop: '10px' }}>
                        <TableContainer>
                {failedEmailList.length === 0 ? (
                    <EmptyMessage>
                        <MdEmail size={48} color="#ccc" />
                        <div style={{ marginTop: "10px" }}>
                            {t("failedEmailSchedule.noData")}
                        </div>
                    </EmptyMessage>
                ) : (
                    <StyledTable>
                        <thead>
                            <tr>
                                {columns.map((column, idx) => (
                                    <th key={idx}>
                                        {column.Header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {failedEmailList.map((email, idx) => (
                                <tr key={idx} onClick={() => handleRowClick(email)}>
                                    {columns.map((column, colIdx) => (
                                        <td key={colIdx}>
                                            {column.Cell
                                                ? column.Cell({ value: email[column.accessor], row: { original: email } })
                                                : email[column.accessor]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </StyledTable>
                )}
            </TableContainer>

                        {/* Pagination */}
                        {totalPages > 0 && (
                            <div style={{
                                width: '400px',
                                height: '50px',
                                marginTop: '10px',
                                marginLeft: 'auto'
                            }}>
                                <PaginationHandler
                                    goBefore={() => {
                                        if (currentPageIndex > 0) {
                                            setCurrentPageIndex(currentPageIndex - 1);
                                        }
                                    }}
                                    goAfter={() => {
                                        if (currentPageIndex < totalPages - 1) {
                                            setCurrentPageIndex(currentPageIndex + 1);
                                        }
                                    }}
                                    goFirst={() => setCurrentPageIndex(0)}
                                    goLast={() => setCurrentPageIndex(totalPages - 1)}
                                    pageSize={12}
                                    currentIndex={currentPageIndex}
                                    maxIndex={totalPages - 1}
                                    totalItemNumber={totalElements}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Row>

            {/* Modals */}
            <FailedEmailScheduleDetailModal
                isOpen={isDetailModalOpen}
                setIsOpen={setIsDetailModalOpen}
                emailData={selectedEmail}
                onRetry={(email) => {
                    setIsDetailModalOpen(false);
                    setActionEmail(email);
                    setIsRetryConfirm(true);
                }}
                onDelete={(email) => {
                    setIsDetailModalOpen(false);
                    setActionEmail(email);
                    setIsDeleteConfirm(true);
                }}
            />

            <ConfirmModal
                isOpen={isRetryConfirm}
                setIsOpen={setIsRetryConfirm}
                title={t("failedEmailSchedule.retryConfirm")}
                confirmLabel={t("button.yes")}
                rejectLabel={t("button.no")}
                onConfirm={handleRetry}
                onReject={() => {
                    setIsRetryConfirm(false);
                    setActionEmail(null);
                }}
                isLoading={isRetrying}
            />

            <ConfirmModal
                isOpen={isDeleteConfirm}
                setIsOpen={setIsDeleteConfirm}
                title={t("failedEmailSchedule.deleteConfirm")}
                confirmLabel={t("button.yes")}
                rejectLabel={t("button.no")}
                onConfirm={handleDelete}
                onReject={() => {
                    setIsDeleteConfirm(false);
                    setActionEmail(null);
                }}
            />
        </Container>
    );
};

export default FailedEmailScheduleManagement;
